import classNames from 'classnames'
import React, { useMemo, KeyboardEvent, MutableRefObject } from 'react'

type CodeFieldProps = {
  codeLength: number
  code: string
  sendCode: (code: string) => void
  setCode: (code: string) => void
  disabled?: boolean
  errorMessage?: string
  containerClassName?: string
  inputFieldClassName?: string
  inputProps?: React.InputHTMLAttributes<HTMLInputElement>
}

export default function CodeField({
  codeLength,
  code,
  sendCode,
  setCode,
  disabled,
  errorMessage,
  containerClassName,
  inputFieldClassName,
  inputProps,
}: CodeFieldProps) {
  const inputRefs: React.Ref<HTMLInputElement>[] = useMemo(
    () =>
      Array(codeLength)
        .fill(0)
        .map(() => React.createRef<HTMLInputElement>()),
    [codeLength],
  )

  const handleInputFocus = (
    event: KeyboardEvent<HTMLInputElement>,
    index: number,
  ) => {
    // 如果按了 backspace，會將刪除所有值並且 focus 回第一個 input 框框
    if (event.code === 'Backspace') {
      setCode('')
      for (let i = 0; i < inputRefs.length; i++) {
        const ref = inputRefs[i] as MutableRefObject<HTMLInputElement>
        ref.current.value = ''
        if (i === 0) ref.current.focus()
      }
      return
    }
    // 如果按的是數字，才進行以下動作，不是則不做任何動作
    if (Number.isInteger(parseInt(event.key, 10))) {
      // 如果位在最後一個 input 框框，就將更新完的值帶入 callback
      if (code.length === codeLength - 1) {
        const newCode = code.concat(event.key)
        setCode(newCode)
        return sendCode(newCode)
      }
      // 如果還有下一個 input 框框，就 focus 到下一個框框並且更新值
      if (inputRefs[index + 1]) {
        const ref = inputRefs[index + 1] as MutableRefObject<HTMLInputElement>
        ref.current.focus()

        const newCode = code.concat(event.key)
        setCode(newCode)
      }
    }
    // TODO 目前如果輸入不是數字或 backspace 沒有特別處理
  }
  return (
    <div
      className={classNames(
        'flex-col justify-items-start',
        containerClassName,
      )}>
      <div>
        {inputRefs.map((ref, index) => {
          return (
            <input
              key={index}
              type="text"
              inputMode="numeric"
              ref={ref}
              maxLength={1}
              disabled={disabled}
              onKeyUp={event => {
                handleInputFocus(event, index)
              }}
              {...inputProps}
              className={classNames(
                'w-10 h-12 mx-eXxs',
                'rounded-lg border border-text600 caret-primary900 text-paragraph text-center',
                'focus:shadow-none focus:border-text600 focus:outline-none',
                {
                  'border-alert-danger focus:border-alert-danger':
                    !!errorMessage,
                },
                { 'cursor-not-allowed': disabled },
                inputFieldClassName,
                inputProps?.className,
              )}
            />
          )
        })}
      </div>
      {errorMessage && (
        <p className="babu-text-fineprint text-alert-danger mt-xxs">
          {errorMessage}
        </p>
      )}
    </div>
  )
}
